/**
 * useOfflineData Hook
 * 
 * React hook for components to access offline state and data.
 * Provides:
 * - Connection status (online/offline/syncing)
 * - Pending action counts
 * - Cached route data
 * - Optimistic update helpers
 * 
 * @module hooks/useOfflineData
 */

import { useState, useEffect, useCallback } from 'react';
import { offlineStorage } from '../services/offlineStorage';
import { connectionManager } from '../services/connectionManager';

/**
 * Hook for offline-aware data access
 * 
 * @param {Object} options - Hook options
 * @param {boolean} options.subscribeToStorage - Subscribe to storage changes (default: true)
 * @returns {Object} Offline state and helpers
 */
export const useOfflineData = (options = {}) => {
  const { subscribeToStorage = true } = options;

  // Connection state
  const [isOnline, setIsOnline] = useState(connectionManager.online);
  const [isSyncing, setIsSyncing] = useState(connectionManager.isSyncing);
  const [pendingCount, setPendingCount] = useState(offlineStorage.getPendingCount());
  const [criticalPending, setCriticalPending] = useState(offlineStorage.getCriticalPendingCount());
  
  // Route data state
  const [routeData, setRouteData] = useState(null);
  const [hasOptimisticUpdates, setHasOptimisticUpdates] = useState(false);
  const [lastSync, setLastSync] = useState(offlineStorage.getLastSync());

  // Subscribe to connection state changes
  useEffect(() => {
    const unsubscribe = connectionManager.onStateChange((state) => {
      setIsOnline(state.online);
      setIsSyncing(state.syncing);
      setPendingCount(state.pendingCount);
      setCriticalPending(state.criticalPending);
    });

    return unsubscribe;
  }, []);

  // Subscribe to storage changes
  useEffect(() => {
    if (!subscribeToStorage) return;

    const unsubscribe = offlineStorage.subscribe((eventType, data) => {
      switch (eventType) {
        case 'routeData':
          setRouteData(data?.data || null);
          setHasOptimisticUpdates(false);
          break;
        case 'optimisticUpdate':
          setRouteData(data);
          setHasOptimisticUpdates(true);
          break;
        case 'actionQueued':
        case 'actionRemoved':
        case 'actionsCleared':
          setPendingCount(offlineStorage.getPendingCount());
          setCriticalPending(offlineStorage.getCriticalPendingCount());
          break;
        default:
          break;
      }
    });

    // Load initial route data
    const cached = offlineStorage.getRouteData();
    if (cached) {
      setRouteData(cached);
      setHasOptimisticUpdates(offlineStorage.hasOptimisticUpdates());
    }

    // Update last sync
    setLastSync(offlineStorage.getLastSync());

    return unsubscribe;
  }, [subscribeToStorage]);

  // Manual refresh of state
  const refresh = useCallback(() => {
    setIsOnline(connectionManager.online);
    setIsSyncing(connectionManager.isSyncing);
    setPendingCount(offlineStorage.getPendingCount());
    setCriticalPending(offlineStorage.getCriticalPendingCount());
    setLastSync(offlineStorage.getLastSync());
    
    const cached = offlineStorage.getRouteData();
    if (cached) {
      setRouteData(cached);
      setHasOptimisticUpdates(offlineStorage.hasOptimisticUpdates());
    }
  }, []);

  // Force sync pending actions
  const forceSync = useCallback(() => {
    connectionManager.forceSync();
  }, []);

  // Manual reconnect
  const reconnect = useCallback(() => {
    connectionManager.reconnect();
  }, []);

  // Apply optimistic update to route data
  const applyOptimisticUpdate = useCallback((updateFn) => {
    offlineStorage.applyOptimisticUpdate(updateFn);
  }, []);

  // Get diagnostic info
  const getDiagnostics = useCallback(() => {
    return {
      ...connectionManager.getState(),
      ...offlineStorage.getDiagnostics(),
    };
  }, []);

  return {
    // Connection state
    isOnline,
    isSyncing,
    
    // Pending actions
    pendingCount,
    criticalPending,
    hasPendingActions: pendingCount > 0,
    
    // Route data
    routeData,
    hasOptimisticUpdates,
    lastSync,
    
    // Computed state
    isOfflineMode: !isOnline && routeData !== null,
    needsSync: pendingCount > 0 && isOnline,
    
    // Actions
    refresh,
    forceSync,
    reconnect,
    applyOptimisticUpdate,
    getDiagnostics,
  };
};

/**
 * Hook specifically for connection status display
 * Lighter weight than useOfflineData
 * 
 * @returns {Object} Connection status
 */
export const useConnectionStatus = () => {
  const [status, setStatus] = useState({
    online: connectionManager.online,
    syncing: connectionManager.isSyncing,
    pendingCount: offlineStorage.getPendingCount(),
    criticalPending: offlineStorage.getCriticalPendingCount(),
  });

  useEffect(() => {
    const unsubscribe = connectionManager.onStateChange((state) => {
      setStatus({
        online: state.online,
        syncing: state.syncing,
        pendingCount: state.pendingCount,
        criticalPending: state.criticalPending,
      });
    });

    return unsubscribe;
  }, []);

  return status;
};

/**
 * Hook for offline action queueing
 * Provides helper to send or queue actions
 * 
 * NOTE: GPS updates are fire-and-forget (not queued) since server doesn't ACK them.
 * Only trip status changes and other critical actions are queued.
 * 
 * @returns {Object} Action helpers
 */
export const useOfflineActions = () => {
  const { isOnline, pendingCount } = useConnectionStatus();

  const sendOrQueue = useCallback(async (type, data, options = {}) => {
    return connectionManager.sendOrQueue(type, data, options);
  }, []);

  return {
    isOnline,
    pendingCount,
    sendOrQueue,
  };
};

export default useOfflineData;
